import { useIntl } from 'gatsby-plugin-react-intl';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { SearchContext } from '../../Providers/SearchIndex';
import Portal from '../Portal';

import {
  Form,
  Overlay,
  Search,
  SearchContainer,
  Submit,
  Wrapper,
} from './styles';

const SearchBar = forwardRef(
  (
    {
      id = null,
      children = null,
      className = '',
      type = '',
      placeholder = null,
      value = '',
      arialabel = 'search',
      onChange = null,
      onSubmit = () => {},
      open = false,
      setOpen = () => {},
      predictiveSearch = false,
    },
    ref,
  ) => {
    const expanded = type === 'is-open' && !predictiveSearch;
    const intl = useIntl();

    const { lunrjs, updateLunrjs } = useContext(SearchContext);

    const {
      localSearch: { publicLunrUrl },
    } = useStaticQuery(graphql`
      query HeaderSearchQuery {
        localSearch {
          publicLunrUrl
        }
      }
    `);

    useEffect(() => {
      const lunrUrl =
        intl.locale === 'en' ? publicLunrUrl.en : publicLunrUrl.fr;
      if (open && !lunrjs[intl.locale].index && !lunrjs[intl.locale].store) {
        fetch(lunrUrl)
          .then(index => index.json())
          .then(ind => {
            const updatedLunrObj = { ...lunrjs[intl.locale], ...ind };
            const updatedLunrLocale = {
              ...lunrjs,
              ...{ [intl.locale]: updatedLunrObj },
            };
            updateLunrjs(updatedLunrLocale);
          });
      }
    }, [intl.locale, open, lunrjs]);

    useEffect(() => {
      if (value.length) {
        setOpen(true);
      }
    }, [value]);

    return (
      <Wrapper className={`${className} ${type}`}>
        <Form
          className={type}
          role="search"
          onSubmit={onSubmit}
          aria-label={arialabel}
          autoComplete="off"
        >
          <SearchContainer>
            {expanded ? (
              <fieldset>
                <legend>
                  <span className="sr-only">
                    {intl.formatMessage({
                      id: 'search',
                    })}
                  </span>
                </legend>
                <label id="search-label" htmlFor={id}>
                  {placeholder}
                </label>
                <div className="search-form" role="status">
                  <Search
                    id={id}
                    placeholder={placeholder}
                    className={`${type} searchable-marker`}
                    value={value}
                    onChange={onChange}
                    onClick={() => setOpen(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setOpen(true);
                      } else if (e.key === 'Escape') {
                        setOpen(false);
                      }
                    }}
                    tabIndex="0"
                    ref={ref}
                    aria-labelledby="search-label"
                  />
                  <Submit
                    type="submit"
                    className="searchable-marker"
                    aria-label={intl.formatMessage({
                      id: 'search',
                    })}
                    open
                    onClick={onSubmit}
                  />
                </div>
              </fieldset>
            ) : (
              <fieldset>
                <legend>
                  <span className="sr-only">
                    {intl.formatMessage({
                      id: 'search',
                    })}
                  </span>
                </legend>
                <label id="search-label" htmlFor={id}>
                  {placeholder}
                </label>
                <div className="search-form">
                  <Search
                    id={id}
                    placeholder={placeholder}
                    className={`${type} ${
                      open ? 'is-open' : ''
                    } searchable-marker`}
                    onClick={() => setOpen(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setOpen(true);
                      } else if (e.key === 'Escape') {
                        setOpen(false);
                      }
                    }}
                    tabIndex="0"
                    value={value}
                    onChange={onChange}
                    aria-label={arialabel}
                    aria-labelledby="search-label"
                    ref={ref}
                  />
                  <Submit
                    type="submit"
                    className="searchable-marker"
                    aria-label={intl.formatMessage({
                      id: 'search',
                    })}
                    open={open}
                    onClick={onSubmit}
                  />
                </div>
              </fieldset>
            )}
          </SearchContainer>
        </Form>
        {children}
        {!expanded && (
          <Portal>
            <Overlay open={open} />
          </Portal>
        )}
      </Wrapper>
    );
  },
);

SearchBar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  id: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  arialabel: PropTypes.string,
  predictiveSearch: PropTypes.bool,
};

export default SearchBar;
